/**
 * StrokeStore - LINKED MASTER STROKES (pass 1)
 *
 * Document-level store of stroke MASTERS plus per-layer INSTANCE tables.
 * Replaces the old "re-clone the live sketch onto every selected layer"
 * stamping, which clobbered layers that had deliberately diverged.
 *
 * Data model
 * ----------
 * - masters: Map<strokeId, shape>  (SketchSpline or SketchPath clones -
 *   the single source of truth for every LINKED stroke)
 * - layers[i]: Array<Instance> in draw order, where an Instance is either
 *     { id, linked: true,  shape: null }   -> resolves from masters
 *     { id, linked: false, shape: <clone> } -> DETACHED standalone copy
 *
 * RESOLUTION (resolveLayerState) materializes plain per-layer sketch
 * states ({paths, splines, closedShapes, legShapes}) so ALL downstream
 * consumers (union, slices, leg braces, exports, meshes) keep reading the
 * exact same shape objects they always did - zero downstream changes.
 *
 * Legs (SketchSpline.TYPE_LEG) live only on the OUTER layers: leg
 * instances are only created on outer layers, and resolution additionally
 * strips them from inner layers (robust when the layer count changes).
 *
 * Sync semantics (syncFromLive - the SYNC stage of regenerateAll):
 * - NEW live shape (unknown strokeId): one master + linked instances on
 *   every selected layer (outer-only for legs).
 * - Edited LINKED shape: with >1 selected layers (or when no other layer
 *   links the same master) the edit writes back to the MASTER, so all
 *   linked layers update. With a single selected layer, if OTHER layers
 *   link the same master, that layer's instance is DETACHED instead
 *   (copy master -> standalone) - the caller is told so it can toast.
 * - Edited DETACHED shape: updates that instance's standalone copy only.
 * - Deleted shape (id on the active layer, missing from the live sketch):
 *   removed from the active layer and from every selected layer whose
 *   instance is LINKED. A detached copy on another layer survives - the
 *   whole point is that diverged layers are never clobbered.
 */
import { SketchSpline } from '../sketch/SketchSpline.js';
import { SketchPath } from '../sketch/SketchPath.js';

let __strokeIdCounter = 0;

/** Generate a document-unique, collision-resistant stroke id */
export function generateStrokeId() {
    __strokeIdCounter++;
    return `sk-${__strokeIdCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Round for signatures: modifier application is inverted on write-back
 * (translate then untranslate, scale then unscale), so float round-trip
 * noise below a micro-pixel must never read as a geometry edit - it
 * would spuriously detach single-selected layers.
 */
function sigRound(v) {
    return Math.round(v * 1e6) / 1e6;
}

/** Canonical point-list signature (ignores volatile per-object ids) */
function pointsSignature(points) {
    return (points || []).map(p => [
        sigRound(p.x), sigRound(p.y),
        p.controlPoint1 ? [sigRound(p.controlPoint1.x), sigRound(p.controlPoint1.y)] : 0,
        p.controlPoint2 ? [sigRound(p.controlPoint2.x), sigRound(p.controlPoint2.y)] : 0
    ]);
}

/**
 * Geometric identity signature for a stroke. Two shapes with equal
 * signatures render/fabricate identically. Per-point widths
 * (spline.centreOffset) are part of the signature, so width edits count
 * as geometry edits (pass 2 will make width a modifier instead).
 * @param {SketchSpline|SketchPath} shape
 * @returns {string}
 */
export function strokeSignature(shape) {
    if (!shape) return 'null';
    if (shape.centrePath) { // SketchSpline
        return JSON.stringify({
            k: 'spline',
            t: shape.type ?? null,
            o: sigRound(shape.offsetSize),
            ot: shape.offsetType,
            ct: shape.capType,
            jt: shape.joinType,
            co: Array.from(shape.centreOffset?.entries?.() || [])
                .map(([i, v]) => [i, sigRound(v)])
                .sort((a, b) => a[0] - b[0]),
            cl: !!shape.centrePath.closed,
            p: pointsSignature(shape.centrePath.points)
        });
    }
    return JSON.stringify({ // SketchPath
        k: 'path',
        cl: !!shape.closed,
        p: pointsSignature(shape.points),
        h: (shape.holes || []).map(h => pointsSignature(h))
    });
}

/** Is this shape a leg stroke? */
function isLegShape(shape) {
    return !!shape && !!shape.centrePath && shape.type === SketchSpline.TYPE_LEG;
}

// =========================================================================
// PARAMETRIC MODIFIER LANE (pass 2)
//
// A modifier lives on the MASTER (keyed by strokeId) and is evaluated at
// each layer's normalized cross-chair position t = layerIndex / (N - 1).
// Resolution applies it to the master clone for LINKED instances only, so
// linked layers fan out parametrically while detached copies stay put and
// every downstream consumer keeps seeing plain resolved shapes.
//
// Curves are user-editable control point lists [{t, v}, ...] (2-5 points,
// t in [0,1]) interpolated with centripetal-flavored uniform Catmull-Rom
// and clamped outside the outermost points.
// =========================================================================

/**
 * Evaluate a modifier curve at parameter t.
 * @param {Array<{t:number, v:number}>} points - control points (any order)
 * @param {number} t - 0..1 cross-chair position
 * @returns {number}
 */
export function evaluateCurve(points, t) {
    if (!points || points.length === 0) return 0;
    const pts = [...points].sort((a, b) => a.t - b.t);
    if (pts.length === 1) return pts[0].v;
    if (t <= pts[0].t) return pts[0].v;
    if (t >= pts[pts.length - 1].t) return pts[pts.length - 1].v;

    // Find the segment containing t
    let i = 0;
    while (i < pts.length - 2 && t > pts[i + 1].t) i++;
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const span = p2.t - p1.t;
    if (span < 1e-9) return p1.v;
    const s = (t - p1.t) / span;

    if (pts.length === 2) {
        // Two points: straight line
        return p1.v + (p2.v - p1.v) * s;
    }

    // Uniform Catmull-Rom on the v values (endpoint-clamped)
    const p0 = pts[Math.max(0, i - 1)];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const s2 = s * s;
    const s3 = s2 * s;
    return 0.5 * (
        (2 * p1.v) +
        (-p0.v + p2.v) * s +
        (2 * p0.v - 5 * p1.v + 4 * p2.v - p3.v) * s2 +
        (-p0.v + 3 * p1.v - 3 * p2.v + p3.v) * s3
    );
}

// -------------------------------------------------------------------------
// CHAIR CURVE (document-level modifier driven by the Layers "Curve" slider)
//
// One {equation, amount, lengthProfile, endAlign} record for the whole
// design: at each layer's t the selected equation gives a weight f(t) in
// [0,1] (0 at the outer layers, peak mid-chair) and every LINKED stroke is
// displaced RADIALLY about its own centre-path centroid. Positive amounts
// swell the middle layers outward (barrel), negative pinch inward (waist).
//
// LENGTH-WISE CONTROLS: the displacement is additionally weighted ALONG
// each stroke by w(s), s in [0,1] being the point's normalized position
// along the centre path:
// - lengthProfile: user control points {s, w} (2-5, Catmull-Rom clamped,
//   default constant 1) -> profile(s).
// - endAlign in [0,1]: tweens the stroke ENDS into alignment across
//   layers ALONG the base stroke's end direction. At each point the
//   displacement's component along the nearest end's BASE TANGENT (the
//   master stroke's end direction, cached with the anchor) is removed by
//   a factor g(s) = endAlign * (1 - fade(s)), where
//   fade(s) = smoothstep(s/0.15) * smoothstep((1-s)/0.15) is EXACTLY 0 at
//   s=0 and s=1. At endAlign=1 the end points keep only their component
//   PERPENDICULAR to the base end tangent: every layer's stroke end sits
//   exactly on the perpendicular line through the base end point (ends
//   line up laterally while still following the curve sideways - they are
//   aligned, not coincident). The falloff is C1-smooth over the first and
//   last 15% of arc length.
// - s is the normalized POINT INDEX along the centre path (i/(n-1)). For
//   brush strokes the path is resampled ~evenly (splinePointEvery), so
//   index fraction ~ arc length - and unlike true arc length it is
//   invariant under the transform itself, which keeps inversion exact.
//
// RADIAL FORMULATION (documented choice): each point scales about a fixed
// per-stroke ANCHOR (centroid + mean point radius r of the master's
// centre path) by k_i = (r + amount*f(t)*w(s_i)) / r, so a point at radius
// rho displaces by (rho/r)*amount*f(t)*w(s_i) in BOTH x and y (a true
// bulge). This behaves far better on thin strokes than a constant-
// magnitude unit-vector radial push: the displacement decays linearly to
// zero at the centroid (affine per point, self-intersection-free),
// whereas a unit push flips direction discontinuously across the centroid
// and folds thin strokes onto themselves.
//
// EXACT INVERTIBILITY: with per-point weights the anchor can no longer be
// recovered from the resolved shape (the centroid is not invariant), so
// the anchor is CACHED per master in the store (StrokeStore.curveAnchors,
// serialized in v2) and passed to both directions: the inverse applies
// the exact reciprocal 1/k_i about the same fixed point, giving
// resolve(writeback(edit)) === edit to float precision. Write-backs KEEP
// the existing anchor (recomputing it from the new master would break the
// reciprocal identity); anchors refresh only when a stroke is created or
// replaced wholesale. Stroke widths (offsetSize) are deliberately NOT
// scaled - the silhouette bulges while material width stays true.
// -------------------------------------------------------------------------

/**
 * Pluggable curve-equation registry: t in [0,1] -> weight in [0,1], all 0
 * at the endpoints (legs on the outer layers never move) and 1 mid-chair.
 * Also exposed as StrokeStore.curveEquations.
 */
export const CURVE_EQUATIONS = {
    /** f(t) = 1 - (2t-1)^2 (default) */
    parabolic: (t) => 1 - (2 * t - 1) ** 2,
    /** f(t) = sin(pi t) */
    sine: (t) => Math.sin(Math.PI * t),
    /** Circular arc (upper half-circle): f(t) = sqrt(1 - (2t-1)^2) */
    arc: (t) => Math.sqrt(Math.max(0, 1 - (2 * t - 1) ** 2))
};

/** Identity chair curve */
export function defaultChairCurve() {
    return {
        equation: 'parabolic',
        amount: 0,
        lengthProfile: [{ s: 0, w: 1 }, { s: 1, w: 1 }],
        endAlign: 0
    };
}

/** Smoothstep clamped to [0,1] */
export function smoothstep01(x) {
    const u = Math.min(1, Math.max(0, x));
    return u * u * (3 - 2 * u);
}

/** Arc span (fraction of the stroke) over which endAlign fades the ends */
export const END_FADE_SPAN = 0.15;

/**
 * Evaluate the length profile ({s, w} control points, Catmull-Rom
 * clamped - same evaluator as the modifier curves) at arc position s.
 * Missing/empty profile = constant 1.
 */
export function evaluateLengthProfile(profile, s) {
    if (!profile || profile.length === 0) return 1;
    return evaluateCurve(profile.map(p => ({ t: p.s, v: p.w })), s);
}

/**
 * End fade: EXACTLY 0 at s=0 and s=1, smoothstepping to 1 over the
 * first/last END_FADE_SPAN of the stroke.
 */
export function endFade(s) {
    return smoothstep01(s / END_FADE_SPAN) * smoothstep01((1 - s) / END_FADE_SPAN);
}

/**
 * Tangential-removal factor at arc position s:
 * g = endAlign * (1 - fade(s)) - the fraction of the displacement's
 * along-end-tangent component that endAlign strips (1 at the very ends
 * when endAlign=1, 0 in the stroke interior).
 */
export function endAlignFactor(curve, s) {
    const ea = Math.min(1, Math.max(0, curve?.endAlign || 0));
    if (ea <= 0) return 0;
    return ea * (1 - endFade(s));
}

/**
 * Compute a stroke's chair-curve scale anchor: centroid + mean point
 * radius of its driving points (centre path for splines, outline for pen
 * paths). Null for degenerate strokes.
 * @returns {{cx:number, cy:number, r:number}|null}
 */
export function computeCurveAnchor(shape) {
    const pts = shape?.centrePath ? shape.centrePath.points : shape?.points;
    if (!pts || pts.length === 0) return null;
    let cx = 0, cy = 0;
    for (const p of pts) { cx += p.x; cy += p.y; }
    cx /= pts.length;
    cy /= pts.length;
    let r = 0;
    for (const p of pts) r += Math.hypot(p.x - cx, p.y - cy);
    r /= pts.length;
    if (r <= 1e-3) return null;

    // BASE end tangents (unit): the stroke's start/end directions in base
    // space, used by endAlign to strip the along-tangent displacement
    const unit = (ax, ay, bx, by) => {
        const dx = bx - ax, dy = by - ay;
        const len = Math.hypot(dx, dy);
        return len > 1e-9 ? [dx / len, dy / len] : [1, 0];
    };
    const n = pts.length;
    const [tsx, tsy] = n > 1 ? unit(pts[0].x, pts[0].y, pts[1].x, pts[1].y) : [1, 0];
    const [tex, tey] = n > 1 ? unit(pts[n - 2].x, pts[n - 2].y, pts[n - 1].x, pts[n - 1].y) : [1, 0];
    return { cx, cy, r, tsx, tsy, tex, tey };
}

/**
 * Apply (dir=+1) or exactly invert (dir=-1) the chair curve ON a shape IN
 * PLACE at cross-chair position t. See the formulation note above.
 *
 * @param {SketchSpline|SketchPath} shape - mutated in place
 * @param {Object} curve - {equation, amount, lengthProfile, endAlign}
 * @param {number} t - normalized cross-chair position (0..1)
 * @param {number} [dir=1] - +1 apply, -1 invert
 * @param {{cx,cy,r}|null} [anchor] - fixed scale anchor. REQUIRED for an
 *        exact inverse (pass the same anchor to both directions); when
 *        omitted it is computed from the shape's current points (fine for
 *        forward application on base geometry).
 * @returns {SketchSpline|SketchPath} the same shape
 */
export function applyChairCurveToShape(shape, curve, t, dir = 1, anchor = null) {
    if (!shape || !curve || !curve.amount) return shape;
    const fn = CURVE_EQUATIONS[curve.equation] || CURVE_EQUATIONS.parabolic;
    const a0 = curve.amount * fn(Math.min(1, Math.max(0, t)));
    if (Math.abs(a0) < 1e-9) return shape;

    const A = anchor || computeCurveAnchor(shape);
    if (!A) return shape; // degenerate stroke: no radial frame
    const { cx, cy, r } = A;
    // Base end tangents for endAlign (older serialized anchors may lack
    // them - endAlign then has no axis and is skipped for safety)
    const hasTangents = Number.isFinite(A.tsx);

    // Per point at arc position s the map is LINEAR about the anchor:
    //   v' = k * v_perp + (k - g*(k-1)) * v_tan
    // where v = p - C decomposed against the nearest end's base tangent T,
    // k = (r + a(s))/r (radial scale, floored) and g = endAlignFactor(s)
    // strips the along-tangent displacement. Component-wise factors make
    // the inverse the exact per-component reciprocal about the same
    // anchor. Note k - g*(k-1) = lerp(k, 1, g) stays positive for any
    // k > 0, so the map never degenerates.
    const factorsAt = (s) => {
        const a = a0 * evaluateLengthProfile(curve.lengthProfile, s);
        let k = (r + a) / r;
        if (!(k > 0.05)) k = 0.05;
        const g = hasTangents ? endAlignFactor(curve, s) : 0;
        const kt = k - g * (k - 1);
        const T = hasTangents
            ? ((s < 0.5) ? [A.tsx, A.tsy] : [A.tex, A.tey])
            : [1, 0]; // g=0 makes T irrelevant, but it must be finite
        return dir >= 0
            ? { kp: k, kt, T }
            : { kp: 1 / k, kt: 1 / kt, T };
    };

    const mapXY = (x, y, f) => {
        const vx = x - cx, vy = y - cy;
        const vt = vx * f.T[0] + vy * f.T[1];
        const px = vx - vt * f.T[0], py = vy - vt * f.T[1];
        return [
            cx + px * f.kp + vt * f.kt * f.T[0],
            cy + py * f.kp + vt * f.kt * f.T[1]
        ];
    };

    const scalePoint = (p, f) => {
        if (!p) return;
        [p.x, p.y] = mapXY(p.x, p.y, f);
        if (p.controlPoint1) {
            [p.controlPoint1.x, p.controlPoint1.y] = mapXY(p.controlPoint1.x, p.controlPoint1.y, f);
        }
        if (p.controlPoint2) {
            [p.controlPoint2.x, p.controlPoint2.y] = mapXY(p.controlPoint2.x, p.controlPoint2.y, f);
        }
    };

    const scaleRing = (pts) => {
        const n = pts.length;
        for (let i = 0; i < n; i++) {
            scalePoint(pts[i], factorsAt(n > 1 ? i / (n - 1) : 0));
        }
    };

    if (shape.centrePath) { // SketchSpline
        scaleRing(shape.centrePath.points || []);
        shape.build?.();
    } else { // SketchPath (holes use their own index fraction)
        scaleRing(shape.points || []);
        for (const hole of shape.holes || []) scaleRing(hole);
    }
    return shape;
}

/** Identity modifier: three flat control points per channel */
export function defaultModifier() {
    return {
        enabled: true,
        offsetX: [{ t: 0, v: 0 }, { t: 0.5, v: 0 }, { t: 1, v: 0 }],
        offsetY: [{ t: 0, v: 0 }, { t: 0.5, v: 0 }, { t: 1, v: 0 }],
        widthScale: [{ t: 0, v: 1 }, { t: 0.5, v: 1 }, { t: 1, v: 1 }]
    };
}

/** Deep-clone a modifier (plain data) */
export function cloneModifier(mod) {
    return mod ? JSON.parse(JSON.stringify(mod)) : null;
}

/** Is this modifier a no-op (identity) at every t? */
export function isIdentityModifier(mod) {
    if (!mod || !mod.enabled) return true;
    const flat = (pts, ident) => (pts || []).every(p => Math.abs(p.v - ident) < 1e-9);
    return flat(mod.offsetX, 0) && flat(mod.offsetY, 0) && flat(mod.widthScale, 1);
}

/**
 * Apply (dir=+1) or invert (dir=-1) a modifier ON a shape IN PLACE at
 * cross-chair position t: translate by (offsetX(t), offsetY(t)) and scale
 * stroke widths by widthScale(t). Splines are rebuilt afterwards.
 * @param {SketchSpline|SketchPath} shape - mutated in place
 * @param {Object} mod - modifier ({enabled, offsetX, offsetY, widthScale})
 * @param {number} t - normalized cross-chair position (0..1)
 * @param {number} [dir=1] - +1 apply, -1 invert
 * @returns {SketchSpline|SketchPath} the same shape
 */
export function applyModifierToShape(shape, mod, t, dir = 1) {
    if (!shape || !mod || !mod.enabled) return shape;

    const dx = evaluateCurve(mod.offsetX, t) * dir;
    const dy = evaluateCurve(mod.offsetY, t) * dir;
    let ws = evaluateCurve(mod.widthScale, t);
    if (!Number.isFinite(ws) || ws < 0.05) ws = 0.05;
    const scale = dir >= 0 ? ws : 1 / ws;

    const movePoint = (p) => {
        if (!p) return;
        p.x += dx;
        p.y += dy;
        if (p.controlPoint1) { p.controlPoint1.x += dx; p.controlPoint1.y += dy; }
        if (p.controlPoint2) { p.controlPoint2.x += dx; p.controlPoint2.y += dy; }
    };

    if (shape.centrePath) { // SketchSpline
        for (const p of shape.centrePath.points || []) movePoint(p);
        if (Math.abs(scale - 1) > 1e-12) {
            shape.offsetSize *= scale;
            if (shape.centreOffset?.size) {
                const scaled = new Map();
                for (const [k, v] of shape.centreOffset) scaled.set(k, v * scale);
                shape.centreOffset = scaled;
            }
        }
        shape.build?.();
    } else { // SketchPath
        for (const p of shape.points || []) movePoint(p);
        for (const hole of shape.holes || []) {
            for (const p of hole) movePoint(p);
        }
    }
    return shape;
}

export class StrokeStore {
    constructor(layerCount = 0) {
        this.masters = new Map();      // strokeId -> master shape
        this.layers = [];              // layerIndex -> Array<Instance>
        this.modifiers = new Map();    // strokeId -> parametric modifier (pass 2)
        this.chairCurve = defaultChairCurve(); // document-level curve slider
        this.curveAnchors = new Map(); // strokeId -> {cx, cy, r} fixed scale anchor
        this.reset(layerCount);
    }

    /** Drop everything and size the per-layer instance tables */
    reset(layerCount) {
        this.masters = new Map();
        this.layers = [];
        this.modifiers = new Map();
        this.chairCurve = defaultChairCurve();
        this.curveAnchors = new Map();
        for (let i = 0; i < layerCount; i++) this.layers.push([]);
    }

    // --- Chair curve (document-level modifier) ----------------------------

    /** The document-level chair curve ({equation, amount, lengthProfile, endAlign}) */
    getChairCurve() {
        return this.chairCurve;
    }

    /** Set the chair curve (normalized deep copy; null resets to identity) */
    setChairCurve(curve) {
        const def = defaultChairCurve();
        if (!curve) {
            this.chairCurve = def;
            return;
        }
        const profile = Array.isArray(curve.lengthProfile) && curve.lengthProfile.length >= 2
            ? curve.lengthProfile.map(p => ({
                s: Math.min(1, Math.max(0, Number(p.s) || 0)),
                w: Number.isFinite(+p.w) ? +p.w : 1
            })).sort((a, b) => a.s - b.s)
            : def.lengthProfile;
        this.chairCurve = {
            equation: curve.equation || def.equation,
            amount: Number(curve.amount) || 0,
            lengthProfile: profile,
            endAlign: Math.min(1, Math.max(0, Number(curve.endAlign) || 0))
        };
    }

    /** Does the chair curve currently displace anything? */
    isChairCurveActive() {
        return !!(this.chairCurve && this.chairCurve.amount);
    }

    /**
     * Update or insert a length-profile control point at arc position s
     * (used by the on-model scrub pill): the nearest existing point within
     * `tol` is updated, otherwise a new point is inserted (up to 5), else
     * the globally nearest point is updated.
     */
    nudgeLengthProfilePoint(s, w, tol = 0.08) {
        const curve = {
            ...this.chairCurve,
            lengthProfile: (this.chairCurve.lengthProfile || defaultChairCurve().lengthProfile)
                .map(p => ({ ...p }))
        };
        const prof = curve.lengthProfile;
        let best = -1;
        let bestD = tol;
        prof.forEach((p, i) => {
            const d = Math.abs(p.s - s);
            if (d <= bestD) { bestD = d; best = i; }
        });
        if (best >= 0) {
            prof[best].w = w;
        } else if (prof.length < 5) {
            prof.push({ s, w });
            prof.sort((a, b) => a.s - b.s);
        } else {
            let ni = 0, nd = Infinity;
            prof.forEach((p, i) => {
                const d = Math.abs(p.s - s);
                if (d < nd) { nd = d; ni = i; }
            });
            prof[ni].w = w;
        }
        this.setChairCurve(curve);
    }

    /**
     * The stroke's FIXED chair-curve scale anchor (lazily computed from
     * the master's base geometry; kept across write-backs - see the
     * invertibility note at applyChairCurveToShape).
     */
    _curveAnchorFor(id) {
        let a = this.curveAnchors.get(id);
        if (!a) {
            a = computeCurveAnchor(this.masters.get(id));
            if (a) this.curveAnchors.set(id, a);
        } else if (!Number.isFinite(a.tsx)) {
            // MIGRATION: anchors serialized before the endAlign axis change
            // carry no base end tangents - backfill them from the master
            // (base geometry) so endAlign has its axis
            const fresh = computeCurveAnchor(this.masters.get(id));
            if (fresh) {
                a = { ...fresh, cx: a.cx, cy: a.cy, r: a.r };
                this.curveAnchors.set(id, a);
            }
        }
        return a || null;
    }

    /**
     * Anchor shifted by the per-stroke modifier's translation at t (the
     * chair curve applies AFTER the modifier, so its scale centre must
     * follow the translated shape; translation preserves the radius).
     */
    _shiftedAnchor(id, mod, t) {
        const a = this._curveAnchorFor(id);
        if (!a) return null;
        if (!mod || !mod.enabled) return a;
        return {
            ...a, // keep r AND the base end tangents (translation-invariant)
            cx: a.cx + evaluateCurve(mod.offsetX, t),
            cy: a.cy + evaluateCurve(mod.offsetY, t)
        };
    }

    // --- Parametric modifiers (pass 2) -----------------------------------

    /** Normalized cross-chair position of a layer (0 at first, 1 at last) */
    layerT(layerIndex) {
        return this.layers.length > 1 ? layerIndex / (this.layers.length - 1) : 0;
    }

    /** The modifier on a master stroke, or null */
    getModifier(id) {
        return this.modifiers.get(id) || null;
    }

    /** Set/replace the modifier on a master stroke (deep-cloned in) */
    setModifier(id, mod) {
        if (!mod) {
            this.modifiers.delete(id);
        } else {
            this.modifiers.set(id, cloneModifier(mod));
        }
    }

    /**
     * Resolve one linked stroke's CONCRETE geometry for a layer: master
     * clone + per-stroke modifier + chair curve, evaluated at the layer's
     * t (in that order). This is both what resolution emits and what the
     * sync diff compares the live sketch against (so modifier/curve-driven
     * per-layer displacement never reads as an edit and never detaches).
     * @returns {SketchSpline|SketchPath|null} a fresh clone
     */
    resolveLinkedShape(id, layerIndex) {
        const master = this.masters.get(id);
        if (!master) return null;
        const clone = this._cloneShape(master);
        const t = this.layerT(layerIndex);
        const mod = this.modifiers.get(id);
        if (mod && mod.enabled) {
            applyModifierToShape(clone, mod, t, 1);
        }
        if (this.isChairCurveActive()) {
            applyChairCurveToShape(clone, this.chairCurve, t, 1,
                this._shiftedAnchor(id, mod, t));
        }
        return clone;
    }

    /** Are per-layer resolved shapes displaced for this stroke? */
    _hasPerLayerDisplacement(id) {
        const mod = this.modifiers.get(id);
        return !!(mod && mod.enabled) || this.isChairCurveActive();
    }

    get layerCount() {
        return this.layers.length;
    }

    /** Grow/shrink the layer tables without touching existing entries */
    ensureLayerCount(count) {
        while (this.layers.length < count) this.layers.push([]);
        if (this.layers.length > count) this.layers.length = count;
    }

    /** Outer layers are the first and last (legs live only there) */
    isOuterLayer(layerIndex) {
        return layerIndex === 0 || layerIndex === this.layers.length - 1;
    }

    /** Clone a shape, preserving its strokeId */
    _cloneShape(shape) {
        const c = shape.clone();
        c.strokeId = shape.strokeId;
        return c;
    }

    /** Does any master or instance carry this id? */
    knowsStroke(id) {
        if (this.masters.has(id)) return true;
        return this.layers.some(list => list.some(inst => inst.id === id));
    }

    _findInstance(layerIndex, id) {
        const list = this.layers[layerIndex];
        return list ? list.find(inst => inst.id === id) : undefined;
    }

    /** Number of layers (excluding `exceptLayer`) with a LINKED instance of id */
    _linkedLayerCountExcept(id, exceptLayer) {
        let n = 0;
        for (let i = 0; i < this.layers.length; i++) {
            if (i === exceptLayer) continue;
            if (this.layers[i].some(inst => inst.id === id && inst.linked)) n++;
        }
        return n;
    }

    /** Layer indices holding a LINKED instance of this id */
    linkedLayersOf(id) {
        const out = [];
        for (let i = 0; i < this.layers.length; i++) {
            if (this.layers[i].some(inst => inst.id === id && inst.linked)) out.push(i);
        }
        return out;
    }

    /** Drop masters (and their modifiers) no layer links any more */
    pruneOrphanMasters() {
        for (const id of [...this.masters.keys()]) {
            const linkedSomewhere = this.layers.some(list =>
                list.some(inst => inst.id === id && inst.linked));
            if (!linkedSomewhere) {
                this.masters.delete(id);
                this.modifiers.delete(id);
                this.curveAnchors.delete(id);
            }
        }
    }

    /**
     * Register a brand-new stroke: one master + linked instances on the
     * given layers (outer layers only for legs). Assigns a strokeId if
     * the shape has none. Mutates the passed live shape's strokeId.
     * @returns {string} The stroke id
     */
    addStroke(shape, layerIndices) {
        const id = shape.strokeId || generateStrokeId();
        shape.strokeId = id;
        this.masters.set(id, this._cloneShape(shape));
        this.curveAnchors.delete(id); // fresh base geometry -> fresh anchor (lazy)
        const leg = isLegShape(shape);
        for (const li of layerIndices) {
            if (li < 0 || li >= this.layers.length) continue;
            if (leg && !this.isOuterLayer(li)) continue;
            if (!this._findInstance(li, id)) {
                this.layers[li].push({ id, linked: true, shape: null });
            }
        }
        return id;
    }

    /**
     * SYNC: diff the live sketch's committed shapes against the store.
     * See the module doc comment for the exact edit/detach/delete rules.
     *
     * @param {Array} liveShapes - committed live shapes (splines then
     *        paths, draw order). New shapes get a strokeId assigned.
     * @param {number[]} selectedIndices - selected layer indices
     * @param {number} activeIndex - the layer the live sketch mirrors
     *        (MUST be one of selectedIndices)
     * @returns {string[]} ids detached on the active layer by this sync
     */
    syncFromLive(liveShapes, selectedIndices, activeIndex) {
        const detachedIds = [];
        const liveIds = new Set();
        const multiSelect = selectedIndices.length > 1;

        for (const shape of liveShapes) {
            if (!shape) continue;

            // NEW stroke (no id, or an id the store has never seen -
            // e.g. shapes restored from an old clipboard)
            if (!shape.strokeId || !this.knowsStroke(shape.strokeId)) {
                const id = this.addStroke(shape, selectedIndices);
                liveIds.add(id);
                continue;
            }

            const id = shape.strokeId;
            liveIds.add(id);
            const inst = this._findInstance(activeIndex, id);
            if (!inst) continue; // live shows a stroke this layer doesn't carry

            if (inst.linked) {
                const master = this.masters.get(id);
                if (!master) {
                    // Orphan link - heal by rebasing the master on the live shape
                    this.masters.set(id, this._cloneShape(shape));
                    this.curveAnchors.delete(id);
                    continue;
                }
                // Compare against the RESOLVED geometry for the active
                // layer (master + per-stroke modifier + chair curve at
                // this layer's t): parametric fan-out is NOT a user edit
                // and must never detach
                const mod = this.modifiers.get(id);
                const hasMod = !!(mod && mod.enabled);
                const displaced = this._hasPerLayerDisplacement(id);
                const expected = displaced
                    ? this.resolveLinkedShape(id, activeIndex)
                    : master;
                if (strokeSignature(shape) === strokeSignature(expected)) continue;

                const sharedElsewhere = this._linkedLayerCountExcept(id, activeIndex) > 0;
                if (!multiSelect && sharedElsewhere) {
                    // Single-layer GEOMETRY edit on a shared linked stroke:
                    // DETACH this layer's instance (never touch the master).
                    // The detached copy is the live = RESOLVED geometry for
                    // this layer (modifier baked in), and the modifier no
                    // longer applies to it.
                    inst.linked = false;
                    inst.shape = this._cloneShape(shape);
                    detachedIds.push(id);
                } else {
                    // Linked edit: write back to the MASTER so every linked
                    // layer updates. The live sketch shows the RESOLVED
                    // geometry, so INVERT the transforms at the active
                    // layer's t in reverse application order (chair curve
                    // first - about the SAME kept anchor, exact reciprocal -
                    // then the per-stroke modifier) to recover base space.
                    const base = this._cloneShape(shape);
                    const t = this.layerT(activeIndex);
                    if (this.isChairCurveActive()) {
                        applyChairCurveToShape(base, this.chairCurve, t, -1,
                            this._shiftedAnchor(id, hasMod ? mod : null, t));
                    }
                    if (hasMod) {
                        applyModifierToShape(base, mod, t, -1);
                    }
                    // KEEP the existing curve anchor: recomputing it from
                    // the new master would break resolve(writeback) == edit
                    this.masters.set(id, base);
                }
            } else {
                // Detached instance: the live sketch IS this layer's copy
                if (strokeSignature(shape) !== strokeSignature(inst.shape)) {
                    inst.shape = this._cloneShape(shape);
                }
            }
        }

        // Deletions: ids on the ACTIVE layer that vanished from the live
        // sketch. Legs are exempt while an INNER layer is active - inner
        // resolved states legitimately omit legs, that's not a deletion.
        const activeOuter = this.isOuterLayer(activeIndex);
        const activeList = this.layers[activeIndex] || [];
        const deletedIds = activeList
            .filter(inst => {
                if (liveIds.has(inst.id)) return false;
                const src = inst.linked ? this.masters.get(inst.id) : inst.shape;
                if (isLegShape(src) && !activeOuter) return false;
                return true;
            })
            .map(inst => inst.id);

        for (const id of deletedIds) {
            for (const li of selectedIndices) {
                const list = this.layers[li];
                if (!list) continue;
                const k = list.findIndex(inst => inst.id === id &&
                    (li === activeIndex || inst.linked));
                if (k >= 0) list.splice(k, 1);
            }
        }

        this.pruneOrphanMasters();
        return detachedIds;
    }

    /**
     * Replace the given layers' strokes wholesale (paste / v1-file load):
     * every shape becomes a master linked on all the given layers
     * (dedupe: one master shared across the layers), legs outer-only.
     * Shapes without a strokeId get one assigned.
     */
    replaceLayerStrokes(layerIndices, shapes) {
        for (const li of layerIndices) {
            if (li >= 0 && li < this.layers.length) this.layers[li] = [];
        }
        for (const shape of shapes) {
            if (!shape) continue;
            this.addStroke(shape, layerIndices);
        }
        this.pruneOrphanMasters();
    }

    /** Remove ALL instances from the given layers (Clear button) */
    clearLayers(layerIndices) {
        for (const li of layerIndices) {
            if (li >= 0 && li < this.layers.length) this.layers[li] = [];
        }
        this.pruneOrphanMasters();
    }

    /**
     * Insert a new layer at `index`, copying instances from `copyFrom`
     * (linked instances share the master; detached ones are cloned).
     */
    insertLayer(index, copyFrom = null) {
        let list = [];
        if (copyFrom !== null && this.layers[copyFrom]) {
            list = this.layers[copyFrom].map(inst => ({
                id: inst.id,
                linked: inst.linked,
                shape: inst.shape ? this._cloneShape(inst.shape) : null
            }));
        }
        this.layers.splice(Math.max(0, Math.min(index, this.layers.length)), 0, list);
    }

    /**
     * RESOLUTION: materialize a plain sketch state for one layer.
     * Everything downstream consumes these resolved clones - masters and
     * instance shapes are never handed out directly.
     * @returns {{paths:Array, splines:Array, closedShapes:Array, legShapes:Array}}
     */
    resolveLayerState(layerIndex) {
        const keepLegs = this.isOuterLayer(layerIndex);
        const splines = [];
        const paths = [];

        for (const inst of this.layers[layerIndex] || []) {
            const src = inst.linked ? this.masters.get(inst.id) : inst.shape;
            if (!src) continue;
            if (isLegShape(src) && !keepLegs) continue;
            // Linked instances get the parametric modifier evaluated at
            // this layer's t; detached copies are used verbatim
            const clone = inst.linked
                ? this.resolveLinkedShape(inst.id, layerIndex)
                : this._cloneShape(src);
            if (!clone) continue;
            clone.strokeId = inst.id;
            if (clone.centrePath) splines.push(clone);
            else paths.push(clone);
        }

        // Derived lists, same order convention as SketchPlane.rebuildShapeLists
        const closedShapes = [
            ...splines
                .filter(s => s.type !== SketchSpline.TYPE_LEG)
                .map(s => s.getPath()),
            ...paths.filter(p => p.getClosed && p.getClosed())
        ];
        const legShapes = splines
            .filter(s => s.type === SketchSpline.TYPE_LEG)
            .map(s => s.getPath());

        return { paths, splines, closedShapes, legShapes };
    }

    // --- Uniform scale (Scale tool) ------------------------------------

    /**
     * Iterate every distinct geometry-bearing shape once: each master, plus
     * every DETACHED instance's own shape (linked instances share a master,
     * already visited). Used by the bounds + scale helpers below.
     * @param {(shape:Object)=>void} cb
     */
    _forEachShape(cb) {
        for (const s of this.masters.values()) cb(s);
        for (const list of this.layers) {
            for (const inst of list) {
                if (inst && !inst.linked && inst.shape) cb(inst.shape);
            }
        }
    }

    /**
     * Axis-aligned bounds of all stroke geometry in SKETCH px (masters +
     * detached instances, including bezier control points). Returns null if
     * there is no geometry. `cx/cy` is the design centroid the Scale tool
     * scales about.
     */
    getSketchBounds() {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        const acc = (p) => {
            if (!p) return;
            if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
        };
        this._forEachShape((s) => {
            if (!s) return;
            if (s.centrePath) { for (const p of s.centrePath.points || []) acc(p); }
            else {
                for (const p of s.points || []) acc(p);
                for (const h of s.holes || []) for (const p of h) acc(p);
            }
        });
        if (!Number.isFinite(minX)) return null;
        return { minX, minY, maxX, maxY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
    }

    /**
     * Uniformly scale ALL stroke geometry about (cx, cy) by factor f, in
     * place: point positions + bezier control points, plus stroke widths
     * (offsetSize and any per-point centreOffset) and the chair-curve radial
     * amount, so the whole design scales proportionally. Curve anchors are
     * cleared (they cache absolute px and are re-derived on the next
     * resolve). Callers should resolve layer states + regenerate afterward.
     * @param {number} cx @param {number} cy @param {number} f
     */
    scaleAbout(cx, cy, f) {
        if (!(f > 0) || !Number.isFinite(f)) return;
        const sp = (p) => {
            if (!p) return;
            p.x = cx + (p.x - cx) * f;
            p.y = cy + (p.y - cy) * f;
            for (const c of [p.controlPoint1, p.controlPoint2]) {
                if (c) { c.x = cx + (c.x - cx) * f; c.y = cy + (c.y - cy) * f; }
            }
        };
        this._forEachShape((s) => {
            if (!s) return;
            if (s.centrePath) { for (const p of s.centrePath.points || []) sp(p); s.build?.(); }
            else {
                for (const p of s.points || []) sp(p);
                for (const h of s.holes || []) for (const p of h) sp(p);
            }
            if (typeof s.offsetSize === 'number') s.offsetSize *= f;
            if (s.centreOffset instanceof Map) {
                for (const [k, v] of s.centreOffset) s.centreOffset.set(k, v * f);
            }
        });
        // Per-stroke modifier translations are absolute px: scale them so a
        // modified stroke keeps its position relative to the rest of the
        // design (widthScale is a ratio - left alone). Without this a stroke
        // carrying an offsetX/offsetY drifts out of alignment as it scales.
        for (const mod of this.modifiers.values()) {
            if (!mod) continue;
            for (const p of mod.offsetX || []) p.v *= f;
            for (const p of mod.offsetY || []) p.v *= f;
        }
        // Chair-curve radial amount is absolute px; scaling it keeps the curve
        // proportional so every layer scales about the same centre (with r and
        // amount both x f, the radial factor k=(r+a)/r is preserved).
        if (this.chairCurve && this.chairCurve.amount) this.chairCurve.amount *= f;
        // SCALE the sticky curve anchors about the same centre - do NOT clear
        // them. The anchor is deliberately kept across edits (invertibility),
        // so it can differ from a fresh recompute of the current master;
        // clearing it forced a recompute that no longer matched the anchor the
        // pre-scale render used, changing k=(r+a)/r and drifting the stroke out
        // of alignment. Scaling {cx,cy,r} by f (tangents are unit dirs, left
        // alone) keeps k exactly invariant.
        for (const anc of this.curveAnchors.values()) {
            if (!anc) continue;
            anc.cx = cx + (anc.cx - cx) * f;
            anc.cy = cy + (anc.cy - cy) * f;
            anc.r *= f;
        }
    }

    // --- Undo snapshots -------------------------------------------------

    /** Deep snapshot of masters + modifiers + chair curve + instances (undo/redo) */
    captureState() {
        return {
            masters: [...this.masters].map(([id, s]) => [id, this._cloneShape(s)]),
            modifiers: [...this.modifiers].map(([id, m]) => [id, cloneModifier(m)]),
            chairCurve: JSON.parse(JSON.stringify(this.chairCurve)),
            curveAnchors: [...this.curveAnchors].map(([id, a]) => [id, { ...a }]),
            layers: this.layers.map(list => list.map(inst => ({
                id: inst.id,
                linked: inst.linked,
                shape: inst.shape ? this._cloneShape(inst.shape) : null
            })))
        };
    }

    /** Restore a captureState() snapshot */
    restoreState(snapshot) {
        if (!snapshot) return;
        this.masters = new Map(snapshot.masters.map(([id, s]) => [id, this._cloneShape(s)]));
        this.modifiers = new Map((snapshot.modifiers || [])
            .map(([id, m]) => [id, cloneModifier(m)]));
        this.setChairCurve(snapshot.chairCurve || null);
        this.curveAnchors = new Map((snapshot.curveAnchors || [])
            .map(([id, a]) => [id, { ...a }]));
        this.layers = snapshot.layers.map(list => list.map(inst => ({
            id: inst.id,
            linked: inst.linked,
            shape: inst.shape ? this._cloneShape(inst.shape) : null
        })));
    }

    // --- Persistence (design schema v2) ---------------------------------

    _shapeToJSON(shape) {
        return {
            kind: shape.centrePath ? 'spline' : 'path',
            data: shape.toJSON()
        };
    }

    _shapeFromJSON(entry, id, parentSketch = null) {
        let shape = null;
        if (entry.kind === 'spline') {
            shape = SketchSpline.fromJSON(entry.data, parentSketch);
        } else {
            shape = SketchPath.fromJSON(parentSketch, entry.data);
        }
        shape.strokeId = id;
        return shape;
    }

    /** Serialize masters (+ modifiers), chair curve + instance tables (schema v2) */
    toJSON() {
        return {
            chairCurve: JSON.parse(JSON.stringify(this.chairCurve)),
            masters: [...this.masters].map(([id, s]) => {
                const entry = { id, ...this._shapeToJSON(s) };
                const mod = this.modifiers.get(id);
                if (mod) entry.modifier = cloneModifier(mod);
                // The fixed scale anchor must survive save/load or the
                // resolved geometry would shift after a reload
                const anchor = this._curveAnchorFor(id);
                if (anchor) entry.curveAnchor = { ...anchor };
                return entry;
            }),
            layers: this.layers.map(list => list.map(inst => inst.linked
                ? { id: inst.id, linked: true }
                : { id: inst.id, linked: false, ...this._shapeToJSON(inst.shape) }))
        };
    }

    /**
     * Load a toJSON() payload, replacing the store's contents.
     * @param {Object} json - toJSON() output
     * @param {SketchPlane|null} parentSketch - parent for revived shapes
     * @returns {boolean} True on success
     */
    loadJSON(json, parentSketch = null) {
        if (!json || !Array.isArray(json.layers)) return false;
        this.masters = new Map();
        this.modifiers = new Map();
        this.curveAnchors = new Map();
        this.layers = [];
        this.setChairCurve(json.chairCurve || null);
        for (const m of json.masters || []) {
            try {
                this.masters.set(m.id, this._shapeFromJSON(m, m.id, parentSketch));
                if (m.modifier) this.modifiers.set(m.id, cloneModifier(m.modifier));
                if (m.curveAnchor) this.curveAnchors.set(m.id, { ...m.curveAnchor });
            } catch (e) {
                console.warn('StrokeStore.loadJSON: skipping bad master', m?.id, e);
            }
        }
        for (const list of json.layers) {
            const instances = [];
            for (const inst of list || []) {
                if (inst.linked) {
                    if (this.masters.has(inst.id)) {
                        instances.push({ id: inst.id, linked: true, shape: null });
                    }
                } else {
                    try {
                        instances.push({
                            id: inst.id,
                            linked: false,
                            shape: this._shapeFromJSON(inst, inst.id, parentSketch)
                        });
                    } catch (e) {
                        console.warn('StrokeStore.loadJSON: skipping bad instance', inst?.id, e);
                    }
                }
            }
            this.layers.push(instances);
        }
        return true;
    }
}

// Pluggable equation registry, exposed on the class as specified
// (StrokeStore.curveEquations) - add new equations here
StrokeStore.curveEquations = CURVE_EQUATIONS;
